<template>
  <div class="sm:w-full md:w4/5 lg:w-3/5 mx-auto md:p-6">
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-4">
        <h2 class="text-xl font-semibold text-white">Save Gifts</h2>
        <p class="text-red-100 text-sm mt-1">Save gifts for {{ friend.name }}...</p>
      </div>
      <!-- GiftList -->
      <div id="loadingDiv" class="flex justify-center items-center pt-24" hidden>
        <div class="loadingio-spinner-eclipse-s4dxav6xm1i flex justify-center items-center">
          <div class="ldio-s4dxav6xm1i">
            <div></div>
          </div>
        </div>
      </div>

      <div id="giftDiv" class="max-w-4xl mx-auto p-6" hidden>
        <!-- Assigned Gifts Section -->
        <div v-if="assignedGifts.length > 0" class="mb-8">
          <h2 class="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <div class="w-1 h-6 bg-blue-500 rounded mr-3"></div>
            Saved Gifts
            <span class="ml-2 text-sm font-normal text-gray-500">({{ assignedGifts.length }})</span>
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div
v-for="gift in assignedGifts" :key="gift.id"
              class="transform transition-all duration-200 hover:scale-[1.01] hover:shadow-md"
>
              <FriendAssignItem :friend="friend" :gift="gift"></FriendAssignItem>
            </div>
          </div>
        </div>

        <!-- Unassigned Gifts Section -->
        <div v-if="unassignedGifts.length > 0" class="mb-8">
          <h2 class="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <div class="w-1 h-6 bg-gray-400 rounded mr-3"></div>
            Available Gifts
            <span class="ml-2 text-sm font-normal text-gray-500">({{ unassignedGifts.length }})</span>
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div
v-for="gift in unassignedGifts" :key="gift.id"
              class="transform transition-all duration-200 hover:scale-[1.01] hover:shadow-md"
>
              <FriendAssignItem :friend="friend" :gift="gift"></FriendAssignItem>
            </div>
          </div>
        </div>

        <!-- Purchased Gifts Section -->
        <div v-if="purchasedGifts.length > 0" class="mb-8">
          <h2 class="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <div class="w-1 h-6 bg-green-500 rounded mr-3"></div>
            Purchased Gifts
            <span class="ml-2 text-sm font-normal text-gray-500">({{ purchasedGifts.length }})</span>
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div
v-for="gift in purchasedGifts" :key="gift.id"
              class="transform transition-all duration-200 hover:scale-[1.01] hover:shadow-md"
>
              <FriendAssignItem :friend="friend" :gift="gift"></FriendAssignItem>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
v-if="assignedGifts.length === 0 && unassignedGifts.length === 0 && purchasedGifts.length === 0"
          class="text-center py-16"
>
          <div class="text-gray-400 text-6xl mb-4">🎁</div>
          <h3 class="text-lg font-medium text-gray-600 mb-2">No gifts available</h3>
          <p class="text-gray-500">Add some gifts to get started!</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API } from 'aws-amplify';

import FriendAssignItem from "@/components/FriendAssignItem.vue";

export default {
  name: "FriendAssign",
  props: {
    friendID: {
      type: String,
      default() {
        return ""
      }
    },
  },
  components: {
    FriendAssignItem,
  },
  data() {
    return {
      giftImages: {},
      gifts: {},
      isLoading: true,
      friend: {},
    }
  },
  computed: {
    assignedGifts() {
      if (!this.gifts || !Array.isArray(this.gifts) || !this.friend || !this.friend.GiftLink) {
        return [];
      }
      const friendGiftStatus = this.friend.GiftLink.items.reduce((acc, item) => {
        acc[item.gift.id] = item.purchased;
        return acc;
      }, {});

      return this.gifts.filter(gift => {
        const isAssigned = gift.id in friendGiftStatus;
        const isPurchased = isAssigned ? friendGiftStatus[gift.id] : false;
        return isAssigned && !isPurchased;
      }).sort((a, b) => a.name.localeCompare(b.name));
    },
    unassignedGifts() {
      if (!this.gifts || !Array.isArray(this.gifts) || !this.friend || !this.friend.GiftLink) {
        return [];
      }
      const friendGiftStatus = this.friend.GiftLink.items.reduce((acc, item) => {
        acc[item.gift.id] = item.purchased;
        return acc;
      }, {});

      return this.gifts.filter(gift => {
        return !(gift.id in friendGiftStatus);
      }).sort((a, b) => a.name.localeCompare(b.name));
    },
    purchasedGifts() {
      if (!this.gifts || !Array.isArray(this.gifts) || !this.friend || !this.friend.GiftLink) {
        return [];
      }
      const friendGiftStatus = this.friend.GiftLink.items.reduce((acc, item) => {
        acc[item.gift.id] = item.purchased;
        return acc;
      }, {});

      return this.gifts.filter(gift => {
        const isAssigned = gift.id in friendGiftStatus;
        const isPurchased = isAssigned ? friendGiftStatus[gift.id] : false;
        return isAssigned && isPurchased;
      }).sort((a, b) => a.name.localeCompare(b.name));
    }
  },
  watch: {
    isLoading() {
      if (this.isLoading === false) {
        console.log("Loading: " + this.isLoading)
        document.querySelector("#giftDiv").removeAttribute("hidden")
        document.querySelector("#loadingDiv").style.display = 'none';
      }
    }
  },
  created() {
    this.gifts = this.getFriends()
  },
  methods: {
    async getFriends() {
      const query = `query MyQuery {
  listFriends {
    items {
      id
      name
      email
      birthday
      reminder
      anniversary
      anniversaryReminder
      updatedAt
      GiftLink {
        items {
          id
          purchased
          createdAt
          updatedAt
          gift {
            name
            id
            price
            url
            image
            description
          }
        }
      }
    }
  }
}`
      let friends = await API.graphql({
        query: query
      });
      var sortedFriends = friends.data.listFriends.items.sort(
        function (a, b) {
          let nameA = a.name.toUpperCase();
          let nameB = b.name.toUpperCase();

          if (nameA < nameB) {
            return -1;
          }

          if (nameA > nameB) {
            return 1;
          }
        }
      );
      this.$store.dispatch("updateFriends", sortedFriends);
      this.friend = this.$store.state.friendMap.find(item => item.id === this.friendID)
      console.log(this.friend)
      this.gifts = this.$store.state.giftMap
      this.isLoading = false;
    },
    sortGifts(gifts, friend) {
      // Create an object with gift ids as keys and their purchased status as values
      const friendGiftStatus = friend.GiftLink.items.reduce((acc, item) => {
        acc[item.gift.id] = item.purchased;
        return acc;
      }, {});

      return gifts.sort((a, b) => {
        const aInFriendGifts = a.id in friendGiftStatus;
        const bInFriendGifts = b.id in friendGiftStatus;
        const aPurchased = aInFriendGifts ? friendGiftStatus[a.id] : false;
        const bPurchased = bInFriendGifts ? friendGiftStatus[b.id] : false;

        // Order: 1) Unpurchased in friendGiftIds, 2) Purchased in friendGiftIds, 3) Not in friendGiftIds
        if (aInFriendGifts && !aPurchased && (!bInFriendGifts || bPurchased)) {
          return -1;
        } else if (bInFriendGifts && !bPurchased && (!aInFriendGifts || aPurchased)) {
          return 1;
        } else if (aInFriendGifts && aPurchased && !bInFriendGifts) {
          return 1;
        } else if (bInFriendGifts && bPurchased && !aInFriendGifts) {
          return -1;
        } else {
          // Fallback to alphabetical sort if other conditions are not met
          return a.name.localeCompare(b.name);
        }
      });
    }

  },
};
</script>
